import { API_URL } from '../config/app.config';
import { setFoodGroups } from '../config/categories';
import { Vegetable, ComboPack, ProductImage, ProductVariant } from '../types/product';

export interface HomepageFarmer {
  id: number;
  name: string;
  village: string | null;
  district: string | null;
  state: string | null;
  available_produce: string | null;
  emoji: string;
  photo_url: string | null;
  story: string | null;
  sort_order: number;
}

export interface PartnerFarmer extends HomepageFarmer {
  show_on_homepage: number;
  joined_at: string | null;
}

export async function fetchFarmers(): Promise<HomepageFarmer[]> {
  try {
    const res = await fetch(`${API_URL}/api/farmers`);
    if (!res.ok) return [];
    const json = await res.json();
    return json.data as HomepageFarmer[];
  } catch {
    return [];
  }
}

export async function fetchAllFarmers(): Promise<PartnerFarmer[]> {
  try {
    const res = await fetch(`${API_URL}/api/farmers/partners`);
    if (!res.ok) return [];
    const json = await res.json();
    return json.data as PartnerFarmer[];
  } catch {
    return [];
  }
}

export interface Testimonial {
  id: number;
  name: string;
  location: string | null;
  society: string | null;
  review: string;
  rating: number;
  sort_order: number;
}

export async function fetchTestimonials(): Promise<Testimonial[]> {
  try {
    const res = await fetch(`${API_URL}/api/testimonials`);
    if (!res.ok) return [];
    const json = await res.json();
    return json.data as Testimonial[];
  } catch {
    return [];
  }
}

export interface Banner {
  id: number;
  title: string | null;
  media_type: 'image' | 'video';
  media_url: string;
  link_url: string | null;
  sort_order: number;
}

export interface DeliverySlot {
  slot_key: string;
  label: string | null;
  day: string;
  time_from: string;
  time_to: string;
}

export async function fetchDeliverySlots(): Promise<DeliverySlot[]> {
  try {
    const res = await fetch(`${API_URL}/api/delivery-slots/public`);
    if (!res.ok) return [];
    const json = await res.json();
    return json.data as DeliverySlot[];
  } catch {
    return [];
  }
}

export interface HomeStats {
  families: number;
  farmers: number;
  products: number;
  avg_rating: number | null;
}

export async function fetchHomeStats(): Promise<HomeStats | null> {
  try {
    const res = await fetch(`${API_URL}/api/stats/public`);
    if (!res.ok) return null;
    const json = await res.json();
    return json.data as HomeStats;
  } catch {
    return null;
  }
}

export async function fetchBanners(pageKey: string): Promise<Banner[]> {
  const res = await fetch(`${API_URL}/api/banners?page_key=${encodeURIComponent(pageKey)}&platform=web`);
  if (!res.ok) return [];
  const json = await res.json();
  return json.data as Banner[];
}

function mapProductImage(row: any): ProductImage {
  return {
    id: row.id,
    productId: row.product_id,
    mediaType: row.media_type as 'image' | 'video',
    mediaUrl: row.media_url,
    altText: row.alt_text || undefined,
    sortOrder: row.sort_order,
    isPrimary: Boolean(row.is_primary),
  };
}

function mapVariant(v: any): ProductVariant {
  return {
    id: v.id,
    weightGrams: v.weight_grams,
    label: v.label,
    price: Number(v.price),
    isActive: Boolean(v.is_active),
    sortOrder: v.sort_order,
  };
}

function mapVegetable(row: any): Vegetable {
  return {
    id: row.id,
    name: row.name,
    nameHi: row.name_hi || '',
    price: Number(row.price),
    mrp: row.mrp != null ? Number(row.mrp) : undefined,
    unit: row.unit,
    category: row.category || 'regular',
    foodGroup: row.food_group || 'veg',
    // Absent on a server that has not run the migration, which reads as "on
    // sale" — the behaviour every product had before this existed.
    comingSoon: Boolean(row.coming_soon),
    farm: row.farm_source || '',
    emoji: row.emoji || '🥬',
    seasonal: Boolean(row.is_seasonal),
    // Rows predating the column, and any API that doesn't send it, read as
    // Tri-state, and undefined (an older API) reads the same as "not decided".
    refundable: row.is_refundable == null ? null : Boolean(row.is_refundable),
    refundNote: row.refund_note ? String(row.refund_note) : null,
    // The web has its own featured flag; `is_featured` is the app's. The fallback
    // covers a server that hasn't run the migration yet.
    featured: Boolean(row.is_featured_web ?? row.is_featured),
    desc: row.description || '',
    stock: Number(row.stock_qty) || 0,
    maxOrderQty: row.max_order_qty != null ? Number(row.max_order_qty) : undefined,
    benefits: row.health_benefits
      ? (typeof row.health_benefits === 'string' ? JSON.parse(row.health_benefits) : row.health_benefits)
      : undefined,
    storage: row.storage_tip || undefined,
    primaryImage: row.primary_image_url || undefined,
    images: (() => {
      if (Array.isArray(row.images)) return row.images.map(mapProductImage);
      const raw = row.images_json;
      if (!raw) return undefined;
      const arr: any[] = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return arr.map(img => ({
        id: img.id,
        productId: row.id,
        mediaType: (img.media_type || 'image') as 'image' | 'video',
        mediaUrl: img.media_url,
        altText: undefined,
        sortOrder: img.sort_order || 0,
        isPrimary: Boolean(img.is_primary),
      }));
    })(),
    variants: (() => {
      if (Array.isArray(row.variants)) return row.variants.map(mapVariant);
      const raw = row.variants_json;
      if (!raw) return undefined;
      const arr: any[] = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return arr.map(mapVariant);
    })(),
    minVariantPrice: row.min_variant_price != null ? Number(row.min_variant_price) : undefined,
  };
}

function mapComboPack(row: any): ComboPack {
  const items = row.items_json
    ? (typeof row.items_json === 'string' ? JSON.parse(row.items_json) : row.items_json)
    : [];
  return {
    id: row.id,
    name: row.name,
    price: Number(row.price),
    originalPrice: Number(row.original_price),
    members: row.members_range || '',
    items,
    emoji: row.emoji || '🧺',
    featured: Boolean(row.is_featured),
  };
}

export async function fetchVegetables(params?: {
  category?: string;
  search?: string;
  seasonal?: boolean;
}): Promise<Vegetable[]> {
  const query = new URLSearchParams();
  if (params?.category && params.category !== 'all') query.set('category', params.category);
  if (params?.search) query.set('search', params.search);
  if (params?.seasonal) query.set('seasonal', 'true');

  const url = `${API_URL}/api/products${query.toString() ? '?' + query.toString() : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch vegetables');
  const json = await res.json();
  return json.data.map(mapVegetable);
}

// Every product, for admin screens that manage things the storefront hides.
//
// The public list filters out counter-only items and pre-order packs, which is
// right for a shop and wrong for an admin tool: a pack still needs a photo, and
// the only place to give it one reads this list. Needs an admin token.
export async function fetchAllProductsAdmin(token: string): Promise<Vegetable[]> {
  const res = await fetch(`${API_URL}/api/products/admin/all`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch products');
  const json = await res.json();
  return json.data.map(mapVegetable);
}

// Active products for the admin Counter Sale — includes offline-only items that
// the public list hides. Needs an admin token.
export async function fetchCounterProducts(token: string): Promise<Vegetable[]> {
  const res = await fetch(`${API_URL}/api/products/admin/sellable`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch products');
  const json = await res.json();
  return json.data.map(mapVegetable);
}

export interface CartGift {
  id: number;
  min_subtotal: number;
  qty: number;
  product_id: number;
  product_name: string;
  unit: string;
  emoji: string;
  /** 1 = still given when a coupon or coins are applied. */
  allow_with_discount: number;
  /** 0 = a free gift. Above 0 = a special-rate add-on the customer opts into.
   *  Required, not optional: optional is what let the mapper drop it and have
   *  every add-on read as free while still compiling. fetchCartGifts always
   *  supplies a number, defaulting a server that predates the column to 0. */
  price: number;
  /** Rungs of a ladder rather than a list. Rows sharing a group COMPETE: the
   *  highest threshold the cart clears wins and the rest are withheld, so
   *  "capsicum 199 / apple 299 / anar 399" is one gift that gets better.
   *  null/absent = awarded on its own merits, alongside anything else. */
  ladder_group?: string | null;
}

/** A rung that beat a lower one, for "you unlocked something better". */
export interface GiftUpgrade { won: CartGift; replaced: CartGift }
/** A rung just out of reach that would REPLACE what the cart already has. */
export interface NextUpgrade { rung: CartGift; replaces: CartGift }

/**
 * The shop's shelves, from the server.
 *
 * Fills the module-level list in config/categories so the plain helpers
 * (groupLabel, groupColor, groupFilters) keep working synchronously for callers
 * with no hook context — table renderers, mostly.
 *
 * Returns whether the server ANSWERED — not whether the list differed. A caller
 * needs to know the shelves are now authoritative, because until then the
 * fallback three are a guess, and treating a guess as the whole truth is how a
 * product on a shelf this build has never heard of looks like an orphan.
 *
 * A failure leaves the fallback shelves in place: a stale shop is a far smaller
 * problem than a shop with no tabs.
 */
export async function fetchFoodGroups(): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}/api/food-groups`);
    if (!res.ok) return false;
    const json = await res.json();
    const rows = (json.data as any[] || []).map(g => ({
      value: g.slug, label: g.label, emoji: g.emoji, color: g.color,
    }));
    if (!rows.length) return false;
    setFoodGroups(rows);
    return true;   // answered, whether or not it differs from what we had
  } catch {
    return false;
  }
}

// Active free-gift tiers (highest threshold first). Public — cart works pre-login.
export async function fetchCartGifts(): Promise<CartGift[]> {
  try {
    // ?addons=1 — this build understands priced rows and renders them as a
    // paid opt-in. Without it the server sends free gifts only, which is what
    // keeps older app builds from advertising a ₹50 add-on as free.
    // ?ladder=1 likewise — this build collapses a ladder_group to its winning
    // rung. Without it the server sends ungrouped rows only, which is what
    // stops an older build showing capsicum AND apple AND anar where the order
    // awards just the anar.
    const res = await fetch(`${API_URL}/api/cart-gifts/active?addons=1&ladder=1`);
    if (!res.ok) return [];
    const json = await res.json();
    // Spread the row, then coerce. The previous version rebuilt each field by
    // name and left `price` and `allow_with_discount` out, so every priced
    // add-on arrived at resolveGift as undefined and was sorted into `free` —
    // the cart promised a ₹5 hari mirch for nothing and the order charged for
    // it. Both fields are optional on CartGift, so nothing failed to compile
    // and the `|| 0` written as NULL-safety swallowed the omission instead.
    //
    // Listing fields to keep is how that happens: a column added on the server
    // is dropped here silently, and the failure shows up as wrong prices rather
    // than as missing data. Numbers still need coercing — MySQL sends DECIMAL
    // as a string, so '5.00' would be a truthy non-number left alone.
    return (json.data as any[]).map(g => ({
      ...g,
      min_subtotal: Number(g.min_subtotal),
      qty: Number(g.qty),
      price: Number(g.price) || 0,
      allow_with_discount: Number(g.allow_with_discount) || 0,
      emoji: g.emoji || '🎁',
    }));
  } catch {
    return [];
  }
}

// Given the item subtotal, return the earned gift (highest tier met) and the next
// tier to unlock (for the "spend ₹X more" nudge). Tiers must be highest-first.
/**
 * Which gifts this cart has earned, and the next one to unlock.
 *
 * Mirrors the award rule in server/routes/orders.js: every tier the subtotal
 * clears, de-duped by product with the largest qty winning. It has to mirror
 * it — if this shows two freebies and the order delivers one, the cart lied.
 *
 * `hasDiscount` reflects a coupon or coins being applied at checkout, which
 * withholds every gift except those flagged allow_with_discount. The cart
 * screen has no discount yet and passes nothing.
 */
/**
 * One rung per ladder — the cart-side twin of server/utils/cartGiftLadder.js.
 *
 * Duplicated rather than imported because this runs on every cart change and
 * cannot ask the server each time. The two must agree: a cart that promises
 * anar and an order that awards capsicum is worse than either on its own. The
 * ordering below is written out for that reason — the server sorts its rows
 * differently, and a ladder resolving one way here and another at checkout is
 * the one bug this must not have.
 */
function collapseLadder(rows: CartGift[]): { kept: CartGift[]; superseded: GiftUpgrade[] } {
  const better = (a: CartGift, b: CartGift) => (
    Number(a.min_subtotal) - Number(b.min_subtotal) ||
    Number(a.qty) - Number(b.qty) ||
    Number(a.id || 0) - Number(b.id || 0)
  );

  const winners = new Map<string, CartGift>();
  for (const row of rows) {
    if (!row.ladder_group) continue;
    const prev = winners.get(row.ladder_group);
    if (!prev || better(row, prev) > 0) winners.set(row.ladder_group, row);
  }
  if (winners.size === 0) return { kept: rows, superseded: [] };

  const runnerUp = new Map<string, CartGift>();
  for (const row of rows) {
    if (!row.ladder_group) continue;
    if (winners.get(row.ladder_group) === row) continue;
    const prev = runnerUp.get(row.ladder_group);
    if (!prev || better(row, prev) > 0) runnerUp.set(row.ladder_group, row);
  }

  const superseded: GiftUpgrade[] = [];
  winners.forEach((won, group) => {
    const replaced = runnerUp.get(group);
    if (replaced) superseded.push({ won, replaced });
  });

  return {
    kept: rows.filter(row => !row.ladder_group || winners.get(row.ladder_group) === row),
    superseded,
  };
}

export function resolveGift(
  tiers: CartGift[],
  subtotal: number,
  hasDiscount = false,
): {
  free: CartGift[]; addons: CartGift[];
  next: CartGift[]; remaining: number;
  nextAddons: CartGift[]; remainingAddons: number;
  upgraded: GiftUpgrade[]; nextUpgrades: NextUpgrade[]; nextAddonUpgrades: NextUpgrade[];
} {
  const sorted = [...tiers].sort((a, b) => b.min_subtotal - a.min_subtotal);

  // Ladders resolve first, before the discount gate and the by-product
  // de-dupe, because that is the order the server uses. A rung withheld later
  // does not promote the rung below it: handing someone the cheaper gift
  // because the better one is out of stock reads as the shop swapping their
  // present.
  const { kept: reachable, superseded } = collapseLadder(
    sorted.filter(t => subtotal >= t.min_subtotal),
  );

  const best = new Map<number, CartGift>();
  for (const t of reachable) {
    if (hasDiscount && !t.allow_with_discount) continue;
    const prev = best.get(t.product_id);
    if (!prev || Number(t.qty) > Number(prev.qty)) best.set(t.product_id, t);
  }

  // Split by price, the way the server does. `|| 0` so a NULL price reads as
  // free rather than as an add-on priced NaN.
  const unlocked = [...best.values()];
  const free = unlocked.filter(t => (Number(t.price) || 0) === 0);
  const addons = unlocked.filter(t => (Number(t.price) || 0) > 0);

  // The nearest threshold not yet reached, and EVERY gift waiting at it. A
  // single row would name one freebie when two are coming — "add ₹200 for
  // dhaniya" when ₹200 also buys the mirch.
  // FREE rows only. The nudge this feeds says "get X free", and a priced
  // add-on is not free — advertising a ₹5 hari mirch that way promises
  // something the order will charge for.
  const freeTiers = sorted.filter(t => (Number(t.price) || 0) === 0);
  const nextThreshold = [...freeTiers].reverse()
    .find(t => subtotal < t.min_subtotal)?.min_subtotal ?? null;

  const nextBest = new Map<number, CartGift>();
  if (nextThreshold != null) {
    for (const t of freeTiers) {
      if (Number(t.min_subtotal) !== Number(nextThreshold)) continue;
      const prev = nextBest.get(t.product_id);
      if (!prev || Number(t.qty) > Number(prev.qty)) nextBest.set(t.product_id, t);
    }
  }

  // The same walk over the priced rows. Kept separate rather than merged into
  // `next`, because the two nudges cannot share a sentence: one ends "free",
  // the other ends "for ₹5".
  const paidTiers = sorted.filter(t => (Number(t.price) || 0) > 0);
  const nextAddonThreshold = [...paidTiers].reverse()
    .find(t => subtotal < t.min_subtotal)?.min_subtotal ?? null;

  const nextAddonBest = new Map<number, CartGift>();
  if (nextAddonThreshold != null) {
    for (const t of paidTiers) {
      if (Number(t.min_subtotal) !== Number(nextAddonThreshold)) continue;
      const prev = nextAddonBest.get(t.product_id);
      if (!prev || Number(t.qty) > Number(prev.qty)) nextAddonBest.set(t.product_id, t);
    }
  }

  // Which ladders the cart has actually WON — a rung that survived the gates
  // above. Needed twice: to say what was superseded, and to tell a nudge that
  // replaces a gift from one that adds another.
  const wonByGroup = new Map<string, CartGift>();
  for (const t of unlocked) if (t.ladder_group) wonByGroup.set(t.ladder_group, t);

  // Only boast about an upgrade the customer is really getting. A winner the
  // coupon rule then withheld is not something they unlocked.
  const upgraded = superseded.filter(u => wonByGroup.get(u.won.ladder_group as string) === u.won);

  // A rung out of reach either ADDS a gift or REPLACES the one already won.
  // The two cannot share a sentence — "also get anar" and "swap your capsicum
  // for anar" are different promises — so they are split here rather than in
  // each screen.
  const splitNext = (rows: CartGift[]) => {
    const adds: CartGift[] = [];
    const upgrades: NextUpgrade[] = [];
    for (const rung of rows) {
      const replaces = rung.ladder_group ? wonByGroup.get(rung.ladder_group) : undefined;
      if (replaces) upgrades.push({ rung, replaces });
      else adds.push(rung);
    }
    return { adds, upgrades };
  };
  const nextSplit = splitNext([...nextBest.values()]);
  const nextAddonSplit = splitNext([...nextAddonBest.values()]);

  return {
    free,
    addons,
    next: nextSplit.adds,
    remaining: nextThreshold != null ? Math.ceil(nextThreshold - subtotal) : 0,
    nextAddons: nextAddonSplit.adds,
    remainingAddons: nextAddonThreshold != null ? Math.ceil(nextAddonThreshold - subtotal) : 0,
    upgraded,
    nextUpgrades: nextSplit.upgrades,
    nextAddonUpgrades: nextAddonSplit.upgrades,
  };
}

/** "100 g 🌶️ Green Chili for ₹5" — a freebie label with its price attached. */
export function addonLabel(g: CartGift): string {
  return `${giftLabel(g)} for ₹${Number(g.price) || 0}`;
}

/** "mirch", "mirch and dhaniya", "mirch, dhaniya and curry patta". */
export function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** "10 g green mirch", not "0.01 kg green mirch". Freebies are small by nature,
 * and a threshold-teaser is the worst place to make someone parse a decimal.
 * Sub-kilo amounts render in grams; every other unit is left alone. */
/**
 * "100 g", not "0.1 kg".
 *
 * A freebie is small by nature, and a decimal makes it read smaller still —
 * "0.1 kg" looks like a rounding error next to a ₹399 threshold, where "100 g"
 * reads as an amount of coriander. Same number, and the one that sounds like
 * something worth crossing the threshold for.
 *
 * Exported because the screens that show a gift do not all want giftLabel's
 * fixed word order: the checkout cards put the emoji, the amount, the name and
 * a FREE badge (or a checkbox and a price) in separate elements. They were each
 * interpolating `{qty} {unit}` by hand as a result, which is how four of them
 * ended up saying 0.1 kg while the cart banner beside them said 100 g.
 */
export function giftQty(g: Pick<CartGift, 'qty' | 'unit'>): string {
  const qty = Number(g.qty);
  return g.unit === 'kg' && qty > 0 && qty < 1
    ? `${Math.round(qty * 1000)} g`
    : `${qty} ${g.unit}`;
}

export function giftLabel(g: CartGift): string {
  return `${giftQty(g)} ${g.emoji} ${g.product_name}`;
}

export async function fetchVegetableById(id: number): Promise<Vegetable> {
  const res = await fetch(`${API_URL}/api/products/${id}`);
  if (!res.ok) throw new Error('Vegetable not found');
  const json = await res.json();
  return mapVegetable(json.data);
}

export async function fetchComboPacks(): Promise<ComboPack[]> {
  const res = await fetch(`${API_URL}/api/products/packs/all`);
  if (!res.ok) throw new Error('Failed to fetch combo packs');
  const json = await res.json();
  return json.data.map(mapComboPack);
}
